import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function PrivacyPolicy() {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="app">
      <header>
        <div className="container">
          <Link to="/" className="logo">
            <i className="fa-solid fa-book-bible"></i> Bible Beacon
          </Link>
          <nav>
            <Link to="/privacy-policy">Privacy Policy</Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="privacy-policy">
          <div className="container" style={{ maxWidth: '1000px' }}>
            <h1>Bible Beacon Privacy Policy</h1>
            <p className="last-updated">Last Updated: June 11, 2025</p>

            <div className="policy-section">
              <h2>1. Introduction</h2>
              <p>
                This Privacy Policy outlines the practices of Bible Beacon ("we," "us," or "our") concerning the collection, use, maintenance, and disclosure of information from users (each, a "User") of the Bible Beacon mobile application ("App"). This Privacy Policy applies to the App and all products and services offered by Bible Beacon.
              </p>
              <p>
                We are committed to safeguarding the privacy of your personal information. This Privacy Policy details our approach to managing your data when you use our App. By accessing or using the App, you acknowledge and agree to the terms of this Privacy Policy. If you do not agree with these terms, please do not use the App.
              </p>
            </div>

            <div className="policy-section">
              <h2>2. Information We Collect</h2>
              <p>
                Bible Beacon is designed with a strong commitment to user privacy, minimizing the collection of personally identifiable information. We collect the following categories of information:
              </p>
              <ul>
                <li><strong>2.1 Personal Information:</strong> We collect personal information that you voluntarily provide when you use certain features of the App. This includes:
                  <ul>
                    <li>User preferences and settings (e.g., translation preferences, font size, theme settings).</li>
                    <li>User-generated content such as notes, bookmarks, saved verses, and verse groups. This data is stored exclusively locally on your device and is not transmitted to our servers.</li>
                  </ul>
                </li>
                <li><strong>2.2 Non-Personal Information:</strong> We may automatically collect non-personal information about your device and how you interact with the App to improve functionality and user experience. This category includes:
                  <ul>
                    <li>Device information (e.g., device model, operating system version, unique device identifiers).</li>
                    <li>App performance data (e.g., crash logs, error reports).</li>
                    <li>General usage patterns (e.g., features accessed, duration of use). This data is anonymized and aggregated where possible.</li>
                  </ul>
                </li>
                <li><strong>2.3 Bible Content:</strong> The Bible texts and associated explanations are either bundled directly within the App or downloaded as needed and stored locally on your device. This content does not constitute personal information.</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>3. How We Use Your Information</h2>
              <p>The information we collect is used for the following purposes:</p>
              <ul>
                <li>To provide, operate, and maintain the functionality of the App.</li>
                <li>To personalize your experience by remembering your preferences and settings.</li>
                <li>To analyze App usage trends and patterns to enhance and optimize user experience.</li>
                <li>To identify, diagnose, and resolve technical issues and bugs within the App.</li>
                <li>To develop and introduce new features and functionalities based on user interaction and feedback.</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>4. Data Storage and Retention</h2>
              <ul>
                <li><strong>4.1 Local Storage:</strong> All user-generated content (including notes, bookmarks, saved verses, and verse groups) is securely stored exclusively on your local device. We do not transmit or store this personal content on our servers.</li>
                <li><strong>4.2 Data Retention:</strong> As personal user-generated content is stored locally on your device, its retention is managed solely by you. Non-personal information and aggregated usage data are retained only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, or as required by applicable law.</li>
              </ul>
            </div>

            <div className="policy-section">
              <h2>5. Data Sharing and Disclosure</h2>
              <p>
                We do not sell, trade, rent, or otherwise transfer your personal information to third parties. We may share non-personal, aggregated, or device-level information with the following entities, strictly for the purposes described:
              </p>
              <ul>
                <li><strong>5.1 Analytics Providers:</strong> We may share anonymized usage data with third-party analytics providers to gain insights into App usage and improve its performance and functionality. These providers are contractually obligated to keep this information confidential and use it only for the purposes for which we disclose it to them.</li>
                <li><strong>5.2 Mobile Platform Providers:</strong> Certain non-personal information may be shared with mobile platform providers (e.g., Apple App Store, Google Play Store) as required for the operation, distribution, and performance analysis of the App within their respective ecosystems.</li>
                <li><strong>5.3 Service Providers:</strong> We may engage third-party companies and individuals to facilitate our App ("Service Providers"), to provide services on our behalf, or to assist us in analyzing how our App is used. These third parties have access to your non-personal information only to perform these tasks on our behalf and are prohibited from disclosing or using it for any other purpose.</li>
              </ul>
              <p>
                We may also disclose information if required to do so by law or in the good faith belief that such action is necessary to comply with a legal obligation, protect and defend our rights or property, prevent or investigate possible wrongdoing in connection with the App, protect the personal safety of Users of the App or the public, or protect against legal liability.
              </p>
            </div>

            <div className="policy-section">
              <h2>6. Third-Party Services</h2>
              <p>
                The App does not directly integrate with or utilize any third-party services that collect information used to identify you. Any usage of third-party functionalities is limited to those inherent to the device's operating system or the mobile platform's core services for app distribution and performance monitoring, where data is typically anonymized and governed by the respective platform's privacy policies.
              </p>
            </div>

            <div className="policy-section">
              <h2>7. Data Security</h2>
              <p>
                All your personal content (notes, bookmarks, saved verses, and verse groups) is stored locally on your device using the secure storage mechanisms provided by your device's operating system (iOS or Android). These mechanisms typically include encryption and sandboxing to protect app-specific data from unauthorized access by other applications or users on the device.
              </p>
              <p>
                While we design the App to leverage these secure local storage capabilities, the overall security of your locally stored data is also influenced by the security settings and practices on your individual device, such as device passcodes and encryption. No method of electronic storage is entirely infallible, and the security of your data depends significantly on maintaining the security of your device.
              </p>
            </div>

            <div className="policy-section">
              <h2>8. Your Rights</h2>
              <p>
                Depending on your jurisdiction and applicable data protection laws, you may have certain rights regarding your personal information. These rights may include:
              </p>
              <ul>
                <li><strong>Right to Access:</strong> The right to request copies of your personal information held by us.</li>
                <li><strong>Right to Rectification:</strong> The right to request that we correct any information you believe is inaccurate or complete information you believe is incomplete.</li>
                <li><strong>Right to Erasure ("Right to be Forgotten"):</strong> The right to request that we erase your personal information, under certain conditions. As your personal user-generated content is stored locally on your device, this right is primarily exercised by deleting the data directly from your device.</li>
                <li><strong>Right to Restrict Processing:</strong> The right to request that we restrict the processing of your personal information, under certain conditions.</li>
                <li><strong>Right to Object to Processing:</strong> The right to object to our processing of your personal information, under certain conditions.</li>
                <li><strong>Right to Data Portability:</strong> The right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.</li>
              </ul>
              <p>
                To exercise any of these rights, please contact us using the contact information provided in Section 11 of this Privacy Policy. We will respond to your request within a reasonable timeframe and in accordance with applicable law.
              </p>
            </div>

            <div className="policy-section">
              <h2>9. Children's Privacy</h2>
              <p>
                Our App is not intended for use by individuals under the age of 13 ("Children"). We do not knowingly collect personally identifiable information from Children. If you are a parent or guardian and you become aware that your Child has provided us with personal information, please contact us. If we become aware that we have collected personal information from Children without verification of parental consent, we will take steps to remove that information from our servers.
              </p>
            </div>

            <div className="policy-section">
              <h2>10. Changes to This Privacy Policy</h2>
              <p>
                We reserve the right to update or modify this Privacy Policy at any time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for any changes. Your continued use of the App after any modifications to this Privacy Policy will constitute your acknowledgment of the modifications and your consent to abide and be bound by the modified Privacy Policy.
              </p>
            </div>

            <div className="policy-section">
              <h2>11. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy, please contact us at: <a href="mailto:yourbiblebeacon@gmail.com">yourbiblebeacon@gmail.com</a>
              </p>
            </div>
          </div>
        </section>
      </main>

      <footer>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <div className="footer-content">
            <div className="footer-logo">
              <i className="fa-solid fa-book-bible"></i> Bible Beacon
            </div>
            <div className="footer-links">
              <Link to="/privacy-policy">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to top button */}
      <a
        href="#"
        className={`scroll-to-top ${showScrollTop ? 'visible' : ''}`}
        onClick={(e) => {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      >
        <i className="fa-solid fa-arrow-up"></i>
      </a>
    </div>
  );
}

export default PrivacyPolicy;